/**
 * pdfPeriod.js : le PDF de session limité à une période analysée (Lot 2, D240).
 *
 * Le PDF lit l'objet de buildPeriodAnalysis déjà calculé pour l'écran : rien
 * n'est recalculé ici. Ce module ne fait que l'en-tête de période (mêmes
 * phrases que le bandeau, via periodText.js), le tri des annotations et le
 * complément du nom de fichier.
 */
import i18n from '../i18n';
import { BRAND, checkPageBreak, drawSectionTitle } from './pdfHelpers';
import { periodBannerText, periodWarningTexts, retainedText } from '../sessions/period/periodText';

// La police intégrée de jsPDF (Helvetica, WinAnsi) n'a pas la flèche U+2192 :
// elle sortirait en caractères parasites. Même repli que la ligne AUC (« -> »).
export const pdfText = (text) => String(text).replace(/→/g, '->');

const t = (key, options) => i18n.t(key, options);

/**
 * Bloc « Analyse d'une période » sous le bandeau du PDF : bornes avec la date,
 * durée sur celle de la session, mesures retenues, mention 24 h, avertissements
 * actifs de l'écran, dans un encadré à filet bleu.
 */
export function drawPeriodHeader(pdf, analysis, yPos, margin, contentWidth, pageHeight) {
  const lines = [
    { text: periodBannerText(analysis, t), style: 'bold', color: BRAND.dark, size: 9.5 },
    { text: retainedText(analysis, t), style: 'normal', color: BRAND.dark, size: 9 },
    { text: t('session.period.reference24h'), style: 'italic', color: BRAND.gray, size: 8 },
    ...periodWarningTexts(analysis, t).map(({ text }) => (
      { text, style: 'bold', color: BRAND.orange, size: 8.5 })),
  ];
  const wrapped = lines.map((line) => ({ ...line, rows: pdf.splitTextToSize(pdfText(line.text), contentWidth - 8) }));
  const height = wrapped.reduce((h, line) => h + line.rows.length * 4.5, 0) + 6;

  yPos = checkPageBreak(pdf, yPos, height + 12, margin, pageHeight);
  yPos = drawSectionTitle(pdf, t('pdf.period.title'), yPos, margin);
  pdf.setFillColor(...BRAND.lightGray);
  pdf.rect(margin, yPos - 4, contentWidth, height, 'F');
  pdf.setFillColor(...BRAND.blue1);
  pdf.rect(margin, yPos - 4, 1.5, height, 'F');
  let y = yPos + 1;
  wrapped.forEach(({ rows, style, color, size }) => {
    pdf.setFontSize(size);
    pdf.setFont('helvetica', style);
    pdf.setTextColor(...color);
    rows.forEach((row) => { pdf.text(row, margin + 5, y); y += 4.5; });
  });
  return yPos + height + 4;
}

const HHMM = /^(\d{1,2}):(\d{2})$/;
const MINUTE_MS = 60000;

/**
 * Instant (ms) du début d'une annotation. Le modèle ne garde que « HH:MM »
 * (heure locale de saisie) : on retient la première occurrence de cette heure
 * à partir du début de la session, à la minute près. Une session de 24 h qui
 * commence à 10:00 place donc « 08:00 » le lendemain. Les anciennes
 * annotations sans startTime gardent leur timestamp. null si illisible.
 */
export function annotationStartMs(annotation, sessionStartMs) {
  const match = HHMM.exec(annotation?.startTime || '');
  if (!match) {
    const legacy = annotation?.timestamp ? new Date(annotation.timestamp).getTime() : NaN;
    return Number.isFinite(legacy) ? legacy : null;
  }
  if (!Number.isFinite(sessionStartMs)) return null;
  const candidate = new Date(sessionStartMs);
  candidate.setHours(Number(match[1]), Number(match[2]), 0, 0);
  // Jour suivant par le calendrier local, pas + 24 h : la nuit du changement
  // d'heure garde la même heure affichée.
  if (candidate.getTime() < sessionStartMs - MINUTE_MS) candidate.setDate(candidate.getDate() + 1);
  return candidate.getTime();
}

/** Annotations dont le début est dans la période (bornes comprises). */
export function annotationsInPeriod(annotations, analysis, sessionStartMs) {
  const { start, end } = analysis.window;
  return (annotations || []).filter((annotation) => {
    const ms = annotationStartMs(annotation, sessionStartMs);
    return ms != null && ms >= start && ms <= end;
  });
}

// « 20261022-1000 » : heure locale du navigateur (D201), comme le bandeau.
const pad = (n) => String(n).padStart(2, '0');
function fileStamp(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

/** Complément du nom de fichier : « periode-20261022-1000-20261022-1800 » (« period » en EN). */
export function periodFilenameSuffix(analysis) {
  return `${t('pdf.period.filenameSlug')}-${fileStamp(analysis.window.start)}-${fileStamp(analysis.window.end)}`;
}
